import React, { useState } from 'react';
import { X, Network, ArrowRightLeft } from 'lucide-react';
import { showError } from '../../utils/toast';
import { CustomDropdown } from '../../components/CustomDropdown ';

interface ConnectionData {
    asset_id: number;
    connection_type: string;
    is_uplink: boolean;
}

interface AddConnectionModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (data: ConnectionData) => void;
    assetId: number | null;
    assetName?: string;
}

const connectionTypeOptions = [
    { label: 'Ethernet', value: 'ethernet' },
    { label: 'Fiber', value: 'fiber' },
    { label: 'Trunk', value: 'trunk' },
    { label: 'Uplink', value: 'uplink' },
];

export default function AddConnectionModal({ open, onClose, onConfirm, assetId, assetName }: AddConnectionModalProps) {
    const [connectionType, setConnectionType] = useState('ethernet');
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!open) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!assetId) {
            showError("Asset ID is missing");
            return;
        }

        setIsSubmitting(true);

        const payload: ConnectionData = {
            asset_id: assetId,
            connection_type: connectionType,
            is_uplink: false,
        };

        await onConfirm(payload);

        setIsSubmitting(false);
        resetForm();
    };

    const resetForm = () => {
        setConnectionType('ethernet');
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
            {/* REMOVED overflow-hidden here and added overflow-visible to allow dropdown to break out */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl max-w-md w-full overflow-visible transform transition-all animate-fadeIn">

                {/* Header - ADDED rounded-t-xl to keep corners smooth since we removed overflow-hidden from parent */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50 rounded-t-xl">
                    <div className="flex items-center gap-3">
                        <div className="bg-blue-500/10 dark:bg-blue-500/20 p-2 rounded-lg">
                            <ArrowRightLeft className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">New Connection</h3>
                            {assetName && <p className="text-xs text-gray-500 dark:text-gray-400">Connecting to: <span className="text-blue-600 dark:text-blue-300 font-medium">{assetName}</span></p>}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-5">

                    {/* Connection Type */}
                    <div className="relative">
                        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            <Network className="w-4 h-4 mr-2 text-purple-500 dark:text-purple-400" />
                            Connection Type <span className="text-red-500 ml-1">*</span>
                        </label>
                        <CustomDropdown
                            data={connectionTypeOptions}
                            selectedValue={connectionType}
                            onSelect={(val) => setConnectionType(val)}
                            placeholder="Select Type"
                            width="w-full"
                            buttonHeight={12}
                        />
                    </div>

                    {/* Footer Buttons */}
                    <div className="flex gap-3 pt-2 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-lg text-sm font-bold transition-all shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    Connect
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
